import Image from 'next/image'
import Link from 'next/link'
import { RateCtaLabel, StarRating } from '@/components/star-rating'
import type { SuggestedEntity } from '@/lib/suggestions'

interface SuggestedEntitiesProps {
  suggestions: SuggestedEntity[]
  columns?: '1' | '2'
  compact?: boolean
}

export function SuggestedEntities({
  suggestions,
  columns = '2',
  compact = false,
}: SuggestedEntitiesProps) {
  if (suggestions.length === 0) return null

  const gridClass =
    columns === '1' ? 'grid grid-cols-1 gap-3' : 'grid grid-cols-1 sm:grid-cols-2 gap-4'

  return (
    <div className={gridClass}>
      {suggestions.map((s) => (
        <Link
          key={s.id}
          href={`/rate/${s.slug}`}
          className="group flex flex-col bg-white border border-slate-200 rounded-lg overflow-hidden hover:border-[#1E4289] hover:shadow-md transition-all"
        >
          <div
            className={`relative w-full bg-slate-100 ${compact ? 'aspect-[16/9]' : 'aspect-[16/10]'}`}
          >
            <Image
              src={s.imageUrl || '/no_image.png'}
              alt={s.name}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-300"
              unoptimized
            />
            {s.parkLabel && (
              <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-[#1E4289] text-white rounded">
                {s.parkLabel}
              </span>
            )}
          </div>
          <div className="flex items-start justify-between gap-2 px-3 py-3">
            <div className="min-w-0">
              <h3 className="font-semibold text-slate-800 group-hover:text-[#1E4289] break-words text-sm sm:text-base">
                {s.name}
              </h3>
              {s.parent?.name && (
                <p className="text-xs text-slate-500">{s.parent.name}</p>
              )}
            </div>
            {s.ratingSummary && s.ratingSummary.totalRatings > 0 && (
              <StarRating rating={s.ratingSummary.currentRating} size="sm" showNumber />
            )}
          </div>
          <div className="px-3 pb-3">
            <RateCtaLabel />
          </div>
        </Link>
      ))}
    </div>
  )
}
